import { useId, useState } from "react";
import { TariffRequest, type Tariff } from "@spf/contracts";
import { api } from "../../lib/client.js";
import { buttonClass } from "../ViewState.js";

interface Draft {
  effectiveFrom: string;
  ratePerKwh: string;
  vatInclusive: boolean;
  vatPercent: string;
}

/** Nigerian VAT, which is what a bill that adds it adds. */
const EMPTY_DRAFT: Draft = {
  effectiveFrom: "",
  ratePerKwh: "",
  vatInclusive: true,
  vatPercent: "7.5",
};

const LABELS: Record<keyof Draft, string> = {
  effectiveFrom: "Starts on",
  ratePerKwh: "Rate per kWh",
  vatInclusive: "The rate includes VAT",
  vatPercent: "VAT (%)",
};

/**
 * The form that adds a rate. The draft holds strings, because a half-typed
 * number is not a number, and is checked against the shared contract -- the
 * schema the server validates with -- only to name the field before a round
 * trip. The server still decides, and its refusal (a date already taken, most
 * likely) is shown as it words it.
 */
export function AddTariff({
  onAdded,
  onEdited,
}: {
  onAdded: (tariff: Tariff) => void;
  onEdited: () => void;
}) {
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const [problem, setProblem] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const headingId = useId();

  const set: Setter = (name, value) => {
    setDraft((current) => ({ ...current, [name]: value }));
    onEdited();
  };

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    const parsed = parse(draft);
    setProblem(parsed.success ? null : firstProblem(parsed.error));
    if (!parsed.success) return;

    setSaving(true);
    try {
      onAdded(await api.addTariff(parsed.data));
      setDraft(EMPTY_DRAFT);
    } catch (error) {
      setProblem(error instanceof Error ? error.message : "Could not add the rate.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <form
      onSubmit={(event) => {
        void submit(event);
      }}
      aria-labelledby={headingId}
      className="flex flex-col gap-5 border-t border-line pt-6"
    >
      <h3 id={headingId} className="text-base font-semibold text-ink">
        Add a rate
      </h3>
      <Fields draft={draft} set={set} />
      <div>
        <button type="submit" className={buttonClass} disabled={saving}>
          {saving ? "Adding…" : "Add rate"}
        </button>
      </div>
      {/* Always mounted: an alert filled after mounting is announced reliably. */}
      <p role="alert" className="text-sm text-danger-ink">
        {problem ?? ""}
      </p>
    </form>
  );
}

type Setter = <K extends keyof Draft>(name: K, value: Draft[K]) => void;

function Fields({ draft, set }: { draft: Draft; set: Setter }) {
  const inclusiveId = useId();
  return (
    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
      <Field
        name="effectiveFrom"
        type="date"
        value={draft.effectiveFrom}
        hint="From midnight on the site's clock. It ends the rate before it."
        set={set}
      />
      <Field
        name="ratePerKwh"
        value={draft.ratePerKwh}
        hint="As printed on the bill, for your band."
        set={set}
      />
      {/* The whole row is the label, at least 44px tall, so the target is
          section 4's size although the box itself is drawn small. */}
      <label
        className="flex min-h-11 cursor-pointer items-center gap-3 self-center text-sm font-medium text-ink"
        htmlFor={inclusiveId}
      >
        <input
          id={inclusiveId}
          name="vatInclusive"
          type="checkbox"
          checked={draft.vatInclusive}
          onChange={(event) => {
            set("vatInclusive", event.target.checked);
          }}
          className="h-5 w-5 accent-accent"
        />
        {LABELS.vatInclusive}
      </label>
      <Field
        name="vatPercent"
        value={draft.vatPercent}
        hint="Added to the rate only when the rate does not include it."
        set={set}
      />
    </div>
  );
}

function Field({
  name,
  type = "text",
  value,
  hint,
  set,
}: {
  name: "effectiveFrom" | "ratePerKwh" | "vatPercent";
  type?: "text" | "date";
  value: string;
  hint: string;
  set: Setter;
}) {
  const id = useId();
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-sm font-medium text-ink" htmlFor={id}>
        {LABELS[name]}
      </label>
      <input
        id={id}
        name={name}
        type={type}
        inputMode={type === "text" ? "decimal" : undefined}
        value={value}
        aria-describedby={`${id}-hint`}
        onChange={(event) => {
          set(name, event.target.value);
        }}
        className="rounded-2xl border border-line bg-page px-4 py-2.5 text-sm text-ink transition-colors focus:border-accent"
      />
      <span id={`${id}-hint`} className="text-xs text-ink-muted">
        {hint}
      </span>
    </div>
  );
}

/**
 * The draft against the shared contract, the schema the server validates
 * with. The server still decides; this only names the field before a round
 * trip.
 */
function parse(draft: Draft) {
  return TariffRequest.safeParse({
    effectiveFrom: draft.effectiveFrom,
    ratePerKwh: numberOf(draft.ratePerKwh),
    vatInclusive: draft.vatInclusive,
    vatPercent: numberOf(draft.vatPercent),
  });
}

/** A typed number, or NaN for what is not one, so the contract names it. */
function numberOf(text: string): number {
  return text.trim() === "" ? Number.NaN : Number(text);
}

/** The first failure, named by its field: one sentence pointing at the thing to fix. */
function firstProblem(error: {
  issues: readonly { path: PropertyKey[]; message: string }[];
}): string {
  const issue = error.issues[0];
  const field = issue?.path[0];
  if (issue === undefined || typeof field !== "string" || !(field in LABELS)) {
    return "That rate is not valid.";
  }
  return `${LABELS[field as keyof Draft]}: ${issue.message}`;
}
